import { useState, useMemo, useCallback } from "react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronUp, GripVertical } from "lucide-react";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import { Button } from "@/v2/components/ui/button";
import { ComboBox } from "@/v2/components/ui/combobox";
import { Badge } from "@/v2/components/ui/badge";
import { AppIcon } from "@/v2/components/EntryCard";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import { cn } from "@/v2/lib/utils";
import { useMattersCache } from "@/shared/cache";
import {
	renderMatterItem,
	renderSelectedMatter,
} from "@/v2/components/entry-form/pickerItems";
import type { SearchableField } from "@/shared/utils/smartSearch";
import type { Entry } from "@/shared/types/entries";
import type { Matter } from "@/shared/cache";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CombineDialogProps {
	entries: Entry[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (ids: number[], matterId: string, mainEntryId: number) => void;
}

// ---------------------------------------------------------------------------
// Sortable entry row
// ---------------------------------------------------------------------------

function SortableRow({
	entry,
	isMain,
	onPromote,
	draggable,
}: {
	entry: Entry;
	isMain: boolean;
	onPromote: () => void;
	draggable: boolean;
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: entry.id, disabled: !draggable });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const time = entry.end_time
		? new Date(entry.end_time).toLocaleTimeString([], {
				hour: "numeric",
				minute: "2-digit",
			})
		: "";

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn(
				"flex items-center gap-3 px-4 py-3 border-b border-border bg-background transition-colors",
				isMain && "border-l-2 border-l-primary",
				isDragging && "opacity-50 z-10 shadow-lg",
			)}
		>
			{/* Drag handle (desktop) or promote button */}
			{isMain ? (
				<Badge variant="default" className="shrink-0 text-xs px-2 py-0.5 border-primary text-primary">
					Main
				</Badge>
			) : (
				<button
					type="button"
					onClick={onPromote}
					className="shrink-0 inline-flex items-center justify-center size-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
					aria-label="Make main entry"
				>
					<ChevronUp className="size-4" />
				</button>
			)}

			{/* App icon */}
			<div className="shrink-0 size-7 flex items-center justify-center [&_svg]:size-7 [&_img]:size-7">
				<AppIcon application={entry.application} />
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<div className="flex items-baseline justify-between gap-2">
					<span className="text-xs text-muted-foreground">{time}</span>
					<span className="text-xs font-medium tabular-nums text-primary">
						{entry.duration}h
					</span>
				</div>
				<p className="text-sm text-foreground truncate">{entry.description || "No narrative"}</p>
			</div>

			{/* Drag handle — desktop only */}
			{draggable && (
				<button
					type="button"
					className="shrink-0 touch-none text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
					{...attributes}
					{...listeners}
				>
					<GripVertical className="size-4" />
				</button>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Plain row (no dnd overhead on mobile)
// ---------------------------------------------------------------------------

function PlainRow({
	entry,
	isMain,
	onPromote,
}: {
	entry: Entry;
	isMain: boolean;
	onPromote: () => void;
}) {
	const time = entry.end_time
		? new Date(entry.end_time).toLocaleTimeString([], {
				hour: "numeric",
				minute: "2-digit",
			})
		: "";

	return (
		<div
			className={cn(
				"flex items-center gap-3 px-4 py-3 border-b border-border bg-background transition-colors",
				isMain && "border-l-2 border-l-primary",
			)}
		>
			{isMain ? (
				<Badge variant="default" className="shrink-0 text-xs px-2 py-0.5 border-primary text-primary">
					Main
				</Badge>
			) : (
				<button
					type="button"
					onClick={onPromote}
					className="shrink-0 inline-flex items-center justify-center size-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
					aria-label="Make main entry"
				>
					<ChevronUp className="size-4" />
				</button>
			)}

			<div className="shrink-0 size-7 flex items-center justify-center [&_svg]:size-7 [&_img]:size-7">
				<AppIcon application={entry.application} />
			</div>

			<div className="flex-1 min-w-0">
				<div className="flex items-baseline justify-between gap-2">
					<span className="text-xs text-muted-foreground">{time}</span>
					<span className="text-xs font-medium tabular-nums text-primary">
						{entry.duration}h
					</span>
				</div>
				<p className="text-sm text-foreground truncate">{entry.description || "No narrative"}</p>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// CombineDialog
// ---------------------------------------------------------------------------

export function CombineDialog({
	entries,
	open,
	onOpenChange,
	onConfirm,
}: CombineDialogProps) {
	const isMobile = useIsMobile();
	const { matters } = useMattersCache();

	const [orderedIds, setOrderedIds] = useState<number[]>([]);
	const [matterId, setMatterId] = useState("");
	const [matterError, setMatterError] = useState("");

	// Sync ordered IDs when entries change (dialog opens with new selection)
	const entryKey = entries.map((e) => e.id).join(",");
	const [lastKey, setLastKey] = useState("");
	if (entryKey !== lastKey && entries.length > 0) {
		setLastKey(entryKey);
		setOrderedIds(entries.map((e) => e.id));
		// Auto-select matter if all entries share the same one
		const uniqueMatters = new Set(entries.map((e) => e.matter_id).filter(Boolean));
		setMatterId(uniqueMatters.size === 1 ? [...uniqueMatters][0]! : "");
		setMatterError("");
	}

	const orderedEntries = useMemo(() => {
		const map = new Map(entries.map((e) => [e.id, e]));
		return orderedIds.map((id) => map.get(id)).filter(Boolean) as Entry[];
	}, [entries, orderedIds]);

	const combinedTotal = useMemo(
		() => orderedEntries.reduce((sum, e) => sum + e.duration, 0),
		[orderedEntries],
	);

	const promote = useCallback((id: number) => {
		setOrderedIds((prev) => {
			const idx = prev.indexOf(id);
			if (idx <= 0) return prev;
			const next = [...prev];
			next.splice(idx, 1);
			next.unshift(id);
			return next;
		});
	}, []);

	// DnD (desktop only)
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		setOrderedIds((prev) => {
			const oldIndex = prev.indexOf(active.id as number);
			const newIndex = prev.indexOf(over.id as number);
			return arrayMove(prev, oldIndex, newIndex);
		});
	}, []);

	const handleConfirm = useCallback(() => {
		if (!matterId) {
			setMatterError("Matter is required");
			return;
		}
		onConfirm(orderedIds, matterId, orderedIds[0]);
	}, [matterId, orderedIds, onConfirm]);

	// Matter search fields
	const matterFields: ((item: Matter) => SearchableField)[] = useMemo(
		() => [
			(m: Matter) => ({ value: m.matter, weight: 1.5 }),
			(m: Matter) => ({ value: m.client, weight: 1.2 }),
			(m: Matter) => ({ value: m.description, weight: 1.0 }),
		],
		[],
	);

	return (
		<ResponsiveDialog open={open} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent
				title="Combine Entries"
				subtitle="Select a matter and choose the main entry"
				footer={
					<div className="flex items-center justify-end">
						<Button onClick={handleConfirm}>Combine</Button>
					</div>
				}
			>
				<div className="flex flex-col gap-4 p-4">
					{/* Matter selector */}
					<div className="grid gap-1.5">
						<ComboBox
							items={matters}
							value={matterId || null}
							onChange={(id) => {
								setMatterId(id);
								setMatterError("");
							}}
							fields={matterFields}
							getKey={(m) => m.matter_id}
							getDisplayLabel={(m) =>
								`${m.matter.trim()} | ${m.description.trim()} - ${m.client.trim()}`
							}
							getSearchValue={(m) => `${m.matter.trim()} ${m.description.trim()}`}
							renderItem={renderMatterItem}
							renderSelected={renderSelectedMatter}
							placeholder="Select matter..."
							error={matterError}
							clearable
							onClear={() => setMatterId("")}
						/>
					</div>

					{/* Entry list */}
					<div className="rounded-lg border border-border overflow-hidden max-h-[340px] overflow-y-auto">
						{isMobile ? (
							// Mobile: tap-to-promote only, no drag
							orderedEntries.map((entry) => (
								<PlainRow
									key={entry.id}
									entry={entry}
									isMain={entry.id === orderedIds[0]}
									onPromote={() => promote(entry.id)}
								/>
							))
						) : (
							// Desktop: tap-to-promote + drag-to-reorder
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={handleDragEnd}
							>
								<SortableContext
									items={orderedIds}
									strategy={verticalListSortingStrategy}
								>
									{orderedEntries.map((entry) => (
										<SortableRow
											key={entry.id}
											entry={entry}
											isMain={entry.id === orderedIds[0]}
											onPromote={() => promote(entry.id)}
											draggable
										/>
									))}
								</SortableContext>
							</DndContext>
						)}
					</div>

					{/* Combined total */}
					<p className="text-sm text-muted-foreground text-right">
						Combined total:{" "}
						<span className="font-medium text-foreground tabular-nums">
							{Math.round(combinedTotal * 100) / 100} hrs
						</span>
					</p>
				</div>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
